import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { TOAST_WITH_ACTION_DURATION } from "@/constants/ui"
import { showDialogInfo } from "@/hooks/use-dialog-info"
import { AgentSummary, FixedPropertyConfig } from "@/types/agent-summary"
import { ClientValidationException } from "@/types/client-exceptions"
import { DayObject, DayRange } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { AvailableRoom } from "@/types/hotels/hotels"
import { PhotoUrl, Place } from "@/types/place"
import { HotelInfo } from "@/types/price-quote"
import { SettingProactiveAgentsTab } from "@/types/settings"
import { Room, TravelerGroup } from "@/types/trip"
import { skipToken } from "@reduxjs/toolkit/query"

import {
  parsePositiveWholeNumber,
  wholeNumberInputValue,
} from "@/lib/agents-samples"
import type { EventPriceAlertProperties } from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import {
  addDays,
  browserToday,
  dayObjectAsUrlParam,
  parseDayParam,
} from "@/lib/date-utils"
import { httpIsCancelled } from "@/lib/exception-utils"
import { currencySymbol, formatPlaceLocale } from "@/lib/format-utils"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { Locale } from "@/lib/locale-hooks"
import { priceAlertDefaultsFromCurrentPrice } from "@/lib/price-alert-defaults"
import { ProactiveAgentsApi } from "@/lib/proactive-agents-api"
import { useRouter } from "@/lib/router"
import { links, routes } from "@/lib/routes"
import { useLazyGetRankedHotelAvailabilityQuery } from "@/lib/storage/api/hotel-api"
import { useGetPlaceQuery } from "@/lib/storage/api/place-api"
import { useAppSelector } from "@/lib/storage/hooks"
import {
  selectPlaceEndDate,
  selectPlaceStartDate,
  selectPlaceTravelers,
} from "@/lib/storage/place-slice"
import { toastManager } from "@/lib/toast"
import {
  roomToTravelerGroup,
  totalPeople,
  travelerGroupToRoom,
} from "@/lib/trip-utils"
import { singleParam } from "@/lib/utils/url"
import { DatesDialog } from "@/components/dates-dialog"
import { EntityIcon } from "@/components/entity-icon"
import IconBed from "@/components/icons/bed"
import IconChevronDown from "@/components/icons/chevron-down"
import IconCross from "@/components/icons/cross"
import IconTrash from "@/components/icons/trash"
import { RoomDialog } from "@/components/room-dialog"
import { TravelersDialog } from "@/components/travelers-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ErrorMessage } from "@/components/ui/error-message"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Photo } from "@/components/ui/photo"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectTriggerDisplayOnly,
} from "@/components/ui/select"
import { TextInput } from "@/components/ui/text-input"
import { IconButton } from "./ui/icon-button"

function isoToDay(s: string) {
  return parseDayParam(s) ?? browserToday()
}

function isPlace(value: Place | HotelInfo): value is Place {
  return "category_label" in value
}

type RoomSelection = {
  key: string
  hotelRoomEntityRef?: string
  provider?: string
  providerRoomId?: string
  roomName: string
  price?: number
  currency?: string
}

function isOpaqueRoomLabel(label: string) {
  return /^rm-[a-z0-9_-]+$/i.test(label.trim())
}

function roomDisplayName(room: AvailableRoom) {
  const title = room.title?.trim()
  if (title && !isOpaqueRoomLabel(title)) return title
  return "Room option"
}

function roomSelectionDetail(
  room: RoomSelection,
  fallbackCurrency: string,
  formatMoney: ReturnType<typeof Locale.useFormatMoney>
) {
  const detailParts = [
    room.price != null
      ? `${formatMoney(
          room.price,
          false,
          room.currency ?? fallbackCurrency
        )}/night`
      : null,
  ].filter(Boolean)
  return detailParts.join(" · ")
}

function roomSelectionKey(config: {
  hotel_room_entity_ref?: string
  provider?: string
  provider_room_id?: string
}) {
  return (
    config.hotel_room_entity_ref ??
    (config.provider && config.provider_room_id
      ? `${config.provider}:${config.provider_room_id}`
      : null)
  )
}

function agentMatchKey(cfg: FixedPropertyConfig | null | undefined): string {
  if (!cfg) return "any"
  return roomSelectionKey(cfg) ?? "any"
}

function roomMatchKey(room: RoomSelection | null): string {
  if (!room) return "any"
  return (
    room.hotelRoomEntityRef ??
    (room.provider && room.providerRoomId
      ? `${room.provider}:${room.providerRoomId}`
      : "any")
  )
}

function roomSelectionFromAgentConfig(
  cfg: FixedPropertyConfig,
  key: string
): RoomSelection {
  return {
    key,
    hotelRoomEntityRef: cfg.hotel_room_entity_ref,
    provider: cfg.provider,
    providerRoomId: cfg.provider_room_id,
    roomName: cfg.room_name ?? "Specific room",
    price: cfg.baseline_price,
    currency: cfg.currency,
  }
}

const PRICE_ALERT_TOAST_ID = "price-alert-save"

interface WatchPriceModalProps {
  place?: Place | HotelInfo
  agent?: AgentSummary
  open: boolean
  onOpenChange: (open: boolean) => void
  knownCurrentPrice?: number
  knownCurrency?: string
  /** Provider-agnostic room identifier (e.g. "rm123"). When set, tracks a specific room type across all providers. */
  hotelRoomEntityRef?: string
  /** Provider name (e.g. "priceline"). Used as fallback room identifier when hotel_room_entity_ref is unavailable. */
  provider?: string
  /** Provider's own room ID. Used with provider as a fallback room identifier. */
  providerRoomId?: string
  /** Room type name shown in the alert list (e.g. "Deluxe King Room"). */
  roomName?: string
  /** First room photo, when known from the room card or quote. */
  roomPhotoUrl?: PhotoUrl | string
  /** Seed dates from the room page so the modal opens with the same dates already in context. */
  initialCheckIn?: DayObject
  initialCheckOut?: DayObject
  initialAdults?: number
  initialChildren?: number
  /** Room occupancy with child ages, when known from the hotel rooms page. */
  room?: Room
  /** Sibling alerts for the same hotel/date range — enables grouped multi-room edit. */
  relatedAgents?: AgentSummary[]
  onCreated?: (agents: AgentSummary[]) => void
  onUpdated?: (agent: AgentSummary) => void
  onDeleted?: (agent: AgentSummary) => void
}

export function WatchPriceModal({
  place,
  agent,
  open,
  onOpenChange,
  knownCurrentPrice,
  knownCurrency,
  hotelRoomEntityRef,
  provider,
  providerRoomId,
  roomName,
  roomPhotoUrl,
  initialCheckIn,
  initialCheckOut,
  initialAdults,
  initialChildren,
  room,
  relatedAgents,
  onCreated,
  onUpdated,
  onDeleted,
}: WatchPriceModalProps) {
  const api = useMemo(() => new ProactiveAgentsApi(buildClientAxios()), [])
  const router = useRouter()
  const formatMoney = Locale.useFormatMoney()
  const t = useTranslation()
  const formatDayObjectAsMonthDay = Locale.useFormatDayObjectAsMonthDay()
  const [fetchRankedAvailability, rankedAvailability] =
    useLazyGetRankedHotelAvailabilityQuery()

  const reduxStart = useAppSelector(selectPlaceStartDate)
  const reduxEnd = useAppSelector(selectPlaceEndDate)
  const travelers = useAppSelector(selectPlaceTravelers)

  // When the modal is seeded from a specific room card (hotelRoomEntityRef +
  // knownCurrentPrice both set), we skip the auto-fetch on open because the
  // cheapest-across-variants price can differ from the room the user clicked.
  // We only fetch once the user explicitly changes dates, adults, or hotel.
  const userModifiedSearchRef = useRef(false)
  const userModifiedTargetRef = useRef(false)

  const [checkIn, setCheckIn] = useState<DayObject | null>(null)
  const [checkOut, setCheckOut] = useState<DayObject | null>(null)
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  const [activeRoom, setActiveRoom] = useState<Room | undefined>(room)
  const [alertMode, setAlertMode] = useState<"fixed" | "percent">("fixed")
  const [targetPrice, setTargetPrice] = useState("")
  const [targetPercent, setTargetPercent] = useState("")
  const [name, setName] = useState("")
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedRoomKeys, setSelectedRoomKeys] = useState<string[]>(["any"])

  const [currentPrice, setCurrentPrice] = useState<number | null | undefined>(
    undefined
  )
  const [priceFetching, setPriceFetching] = useState(false)
  const [priceEntityRef, setPriceEntityRef] = useState<string | null>(null)
  const [currency, setCurrency] = useState("USD")

  // Parents often clear agent/place as soon as open flips false, before the exit
  // animation finishes. Keep the last-open values for rendering until close ends.
  const sessionAgentRef = useRef(agent)
  const sessionPlaceRef = useRef(place)
  const sessionRelatedAgentsRef = useRef(relatedAgents)
  useEffect(() => {
    if (open) {
      sessionAgentRef.current = agent
      sessionPlaceRef.current = place
      sessionRelatedAgentsRef.current = relatedAgents
    }
  }, [open, agent, place, relatedAgents])

  const activeAgent = open ? agent : sessionAgentRef.current
  const activePlace = open ? place : sessionPlaceRef.current
  const activeRelatedAgents = open
    ? relatedAgents
    : sessionRelatedAgentsRef.current

  const isEditing = activeAgent != null
  const isCustomizingAutoCreated =
    isEditing && activeAgent?.created_by === "auto_created"
  const agentCfg =
    activeAgent?.agent_type === "hotel_price_alert"
      ? activeAgent.agent_config
      : null

  const effectiveEntityRef =
    agentCfg?.entity_ref ?? activePlace?.entity_ref ?? null
  const effectiveName = agentCfg?.hotel_name ?? activePlace?.name ?? ""
  const placePhoto =
    activePlace && isPlace(activePlace) ? activePlace.photos?.[0] : undefined
  const placeHasLocale = activePlace != null && isPlace(activePlace)
  const { data: fetchedPlace } = useGetPlaceQuery(
    open &&
      effectiveEntityRef &&
      (activePlace == null || !placePhoto || !placeHasLocale)
      ? { placeRef: effectiveEntityRef as PlaceRef }
      : skipToken
  )
  const propertyPhoto = placePhoto ?? fetchedPlace?.photos?.[0]
  const propertyLocale = fetchedPlace
    ? formatPlaceLocale(fetchedPlace)
    : activePlace && isPlace(activePlace)
      ? formatPlaceLocale(activePlace)
      : ""
  const hasHotel = isEditing || activePlace != null
  const canSelectRoom =
    effectiveEntityRef != null && !hotelRoomEntityRef && !providerRoomId
  const groupAgents = useMemo(
    () =>
      activeRelatedAgents && activeRelatedAgents.length > 0
        ? activeRelatedAgents
        : activeAgent
          ? [activeAgent]
          : [],
    [activeAgent, activeRelatedAgents]
  )
  const isGroupEdit =
    isEditing &&
    canSelectRoom &&
    (activeRelatedAgents?.length ?? 0) > 0 &&
    !isCustomizingAutoCreated
  const groupRoomSelections = useMemo(() => {
    const byKey = new Map<string, RoomSelection>()
    for (const groupAgent of groupAgents) {
      const cfg =
        groupAgent.agent_type === "hotel_price_alert"
          ? groupAgent.agent_config
          : null
      if (!cfg) continue
      const key = agentMatchKey(cfg)
      if (key === "any" || byKey.has(key)) continue
      byKey.set(key, roomSelectionFromAgentConfig(cfg, key))
    }
    return byKey
  }, [groupAgents])

  const resetFormState = useCallback(() => {
    setActionError(null)
    userModifiedSearchRef.current = false
    userModifiedTargetRef.current = false
    if (isGroupEdit) {
      const keys = [
        ...new Set(
          groupAgents.map((groupAgent) => {
            const cfg =
              groupAgent.agent_type === "hotel_price_alert"
                ? groupAgent.agent_config
                : null
            return agentMatchKey(cfg)
          })
        ),
      ]
      const specificKeys = keys.filter((key) => key !== "any")
      setSelectedRoomKeys(specificKeys.length > 0 ? specificKeys : ["any"])
    } else {
      setSelectedRoomKeys(
        agentCfg ? [roomSelectionKey(agentCfg) ?? "any"] : ["any"]
      )
    }
    setCurrentPrice(undefined)
    setPriceEntityRef(null)
    if (agentCfg) {
      setCheckIn(isoToDay(agentCfg.check_in))
      setCheckOut(isoToDay(agentCfg.check_out))
      setAdults(agentCfg.adults)
      setChildren(agentCfg.children)
      setActiveRoom(
        room ??
          (agentCfg.child_ages
            ? {
                adult_count: agentCfg.adults,
                child_ages: agentCfg.child_ages,
                pet_count: 0,
              }
            : travelerGroupToRoom({
                adult_count: agentCfg.adults,
                child_count: agentCfg.children,
                infant_count: 0,
                pet_count: 0,
              }))
      )
      setAlertMode("fixed")
      setTargetPrice(String(agentCfg.target_price))
      setTargetPercent("")
      setName(agent?.name ?? "")
      setCurrency(agentCfg.currency)
    } else {
      const start = initialCheckIn ?? reduxStart ?? addDays(browserToday(), 30)
      const end = initialCheckOut ?? reduxEnd ?? addDays(start, 2)
      setCheckIn(start)
      setCheckOut(end)
      const nextAdults = initialAdults ?? travelers?.adult_count ?? 2
      const nextChildren = initialChildren ?? travelers?.child_count ?? 0
      setAdults(nextAdults)
      setChildren(nextChildren)
      setActiveRoom(
        room ??
          travelerGroupToRoom({
            adult_count: nextAdults,
            child_count: nextChildren,
            infant_count: 0,
            pet_count: 0,
          })
      )
      setAlertMode("fixed")
      const defaultTargets =
        priceAlertDefaultsFromCurrentPrice(knownCurrentPrice)
      setTargetPrice(defaultTargets.targetPrice)
      setTargetPercent(defaultTargets.targetPercent)
      if (place) {
        setName(`${place.name} - Price Alert`)
        setCurrentPrice(knownCurrentPrice ?? undefined)
        setPriceEntityRef(
          knownCurrentPrice !== undefined ? place.entity_ref : null
        )
        setCurrency(knownCurrency ?? "USD")
      } else {
        setName("")
        setCurrency("USD")
      }
    }
  }, [
    agent?.name,
    agentCfg,
    groupAgents,
    initialAdults,
    initialCheckIn,
    initialCheckOut,
    initialChildren,
    isGroupEdit,
    knownCurrency,
    knownCurrentPrice,
    place,
    reduxEnd,
    reduxStart,
    room,
    travelers?.adult_count,
    travelers?.child_count,
  ])

  // Reset only on the closed -> open transition. resetFormState's identity
  // changes whenever the agent prop changes — which happens mid-save when
  // onUpdated/onCreated patch the store — and re-running it while open would
  // re-seed the form mid-edit.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) resetFormState()
    wasOpenRef.current = open
  }, [open, resetFormState])

  const applyDefaultTargetsFromPrice = useCallback(
    (price: number | null | undefined) => {
      if (isEditing || userModifiedTargetRef.current) return
      const defaultTargets = priceAlertDefaultsFromCurrentPrice(price)
      setTargetPrice(defaultTargets.targetPrice)
      setTargetPercent(defaultTargets.targetPercent)
    },
    [isEditing]
  )

  const rankedRoomOptions = useMemo<RoomSelection[]>(() => {
    const embedded = rankedAvailability.data?.availability?.embedded
    const rooms = [
      ...(embedded?.selected ?? []),
      ...(embedded?.similar ?? []),
      ...(embedded?.others ?? []),
    ]
    const seen = new Set<string>()
    return rooms.flatMap((room: AvailableRoom) => {
      const providerRoomId = room.id
      const key =
        room.hotel_room_entity_ref ??
        (providerRoomId ? `${room.provider}:${providerRoomId}` : "")
      if (!key || seen.has(key)) return []
      seen.add(key)
      return [
        {
          key,
          hotelRoomEntityRef: room.hotel_room_entity_ref,
          provider: room.provider,
          providerRoomId,
          roomName: roomDisplayName(room),
          price: room.price_details?.display_price,
          currency: room.price_details?.display_currency,
        },
      ]
    })
  }, [rankedAvailability.data])
  const soleAvailableRoom =
    rankedRoomOptions.length === 1 ? rankedRoomOptions[0] : null
  const roomOptionsLoading =
    canSelectRoom &&
    rankedAvailability.isFetching &&
    rankedRoomOptions.length <= 1
  const showRoomSelector =
    canSelectRoom && !roomOptionsLoading && rankedRoomOptions.length > 1
  const childAgesForPricing = children > 0 ? activeRoom?.child_ages : undefined

  const selectedRooms = useMemo(() => {
    if (selectedRoomKeys.includes("any")) return []
    const rankedByKey = new Map(
      rankedRoomOptions.map((room) => [room.key, room])
    )
    return selectedRoomKeys.flatMap((key) => {
      const ranked = rankedByKey.get(key)
      if (ranked) return [ranked]
      const fallback = groupRoomSelections.get(key)
      return fallback ? [fallback] : []
    })
  }, [groupRoomSelections, rankedRoomOptions, selectedRoomKeys])
  const selectedRoomPrice = useMemo(() => {
    const prices = selectedRooms
      .map((room) => room.price)
      .filter((price): price is number => typeof price === "number")
    return prices.length > 0 ? Math.min(...prices) : undefined
  }, [selectedRooms])
  const selectedRoomCurrency = selectedRooms.find(
    (room) => room.currency
  )?.currency
  const defaultPriceForRoomKeys = useCallback(
    (keys: string[]) => {
      if (keys.includes("any")) return undefined
      const rankedByKey = new Map(
        rankedRoomOptions.map((room) => [room.key, room])
      )
      const prices = keys.flatMap((key) => {
        const room = rankedByKey.get(key) ?? groupRoomSelections.get(key)
        return typeof room?.price === "number" ? [room.price] : []
      })
      return prices.length > 0 ? Math.min(...prices) : undefined
    },
    [groupRoomSelections, rankedRoomOptions]
  )
  const isMultiSpecificRoomSelection = selectedRooms.length > 1
  const preselectedRoom: RoomSelection | null =
    hotelRoomEntityRef ||
    providerRoomId ||
    agentCfg?.hotel_room_entity_ref ||
    agentCfg?.provider_room_id
      ? {
          key: "preselected",
          hotelRoomEntityRef:
            hotelRoomEntityRef ?? agentCfg?.hotel_room_entity_ref,
          provider: provider ?? agentCfg?.provider,
          providerRoomId: providerRoomId ?? agentCfg?.provider_room_id,
          roomName: roomName ?? agentCfg?.room_name ?? "Specific room",
        }
      : null
  const fixedRoom = preselectedRoom ?? soleAvailableRoom
  const hasFixedRoom = fixedRoom != null
  const roomSelectLabel = selectedRoomKeys.includes("any")
    ? "Any room"
    : selectedRooms.length === 1
      ? selectedRooms[0].roomName
      : selectedRooms.length > 1
        ? `${selectedRooms.length} room types selected`
        : (fixedRoom?.roomName ?? "Room type")
  const singleRoomForPrice =
    selectedRooms.length === 1 ? selectedRooms[0] : fixedRoom
  const effectiveHotelRoomEntityRef = singleRoomForPrice?.hotelRoomEntityRef
  const effectiveProvider = singleRoomForPrice?.provider
  const effectiveProviderRoomId = singleRoomForPrice?.providerRoomId
  const effectiveRoomName = singleRoomForPrice?.roomName
  const showRoomTypeSection =
    showRoomSelector || roomOptionsLoading || effectiveRoomName != null

  useEffect(() => {
    if (!open || !canSelectRoom || !effectiveEntityRef || !checkIn || !checkOut)
      return
    void fetchRankedAvailability({
      placeRef: effectiveEntityRef as PlaceRef,
      travelers: {
        adult_count: adults,
        child_count: children,
        infant_count: 0,
        pet_count: 0,
      },
      childAges: childAgesForPricing,
      start: checkIn,
      end: checkOut,
      currency,
    })
  }, [
    open,
    canSelectRoom,
    effectiveEntityRef,
    checkIn,
    checkOut,
    adults,
    children,
    childAgesForPricing,
    currency,
    fetchRankedAvailability,
  ])

  // Live price fetch — runs on open and whenever entity/dates/adults change.
  // When opened from a room card (place + knownCurrentPrice both set), skip
  // the initial fetch — we already have the correct price. Only refetch once
  // the user explicitly changes dates, adults, or hotel.
  useEffect(() => {
    if (!open || !effectiveEntityRef || !checkIn || !checkOut) return
    if (canSelectRoom && selectedRooms.length > 0) return
    if (
      activePlace != null &&
      knownCurrentPrice != null &&
      !userModifiedSearchRef.current
    )
      return
    let cancelled = false
    const abortController = new AbortController()
    const timer = setTimeout(() => {
      setPriceFetching(true)
      void api
        .currentPrice(
          {
            entity_ref: effectiveEntityRef,
            check_in: dayObjectAsUrlParam(checkIn),
            check_out: dayObjectAsUrlParam(checkOut),
            adults,
            children,
            child_ages: childAgesForPricing,
            currency,
            hotel_room_entity_ref: effectiveHotelRoomEntityRef,
            provider: effectiveProvider,
            provider_room_id: effectiveProviderRoomId,
          },
          { signal: abortController.signal }
        )
        .then((res) => {
          if (!cancelled) {
            setCurrentPrice(res.price)
            setPriceEntityRef(res.entity_ref)
            if (res.currency) setCurrency(res.currency)
            applyDefaultTargetsFromPrice(res.price)
          }
        })
        .catch((err) => {
          if (httpIsCancelled(err)) return
          if (!cancelled) setCurrentPrice(null)
        })
        .finally(() => {
          if (!cancelled) setPriceFetching(false)
        })
    }, 600)
    return () => {
      cancelled = true
      abortController.abort()
      clearTimeout(timer)
    }
  }, [
    open,
    effectiveEntityRef,
    checkIn,
    checkOut,
    adults,
    children,
    childAgesForPricing,
    currency,
    api,
    activePlace,
    knownCurrentPrice,
    effectiveHotelRoomEntityRef,
    effectiveProvider,
    effectiveProviderRoomId,
    canSelectRoom,
    selectedRooms.length,
    applyDefaultTargetsFromPrice,
  ])

  const pct = parseFloat(targetPercent)
  const displayCurrentPrice =
    selectedRooms.length > 0 ? selectedRoomPrice : currentPrice
  const displayCurrency = selectedRoomCurrency ?? currency
  const effectivePriceEntityRef =
    selectedRooms.length > 0 && selectedRoomPrice !== undefined
      ? effectiveEntityRef
      : priceEntityRef
  const percentBasePrice =
    selectedRooms.length > 0 ? selectedRoomPrice : currentPrice
  const percentTarget =
    typeof percentBasePrice === "number" &&
    Number.isFinite(pct) &&
    pct > 0 &&
    pct < 100
      ? Math.floor(percentBasePrice * (1 - pct / 100))
      : NaN
  const target =
    alertMode === "percent"
      ? percentTarget
      : parsePositiveWholeNumber(targetPrice)
  const canSavePercentTarget =
    Number.isFinite(pct) &&
    pct > 0 &&
    pct < 100 &&
    (selectedRooms.length > 0
      ? selectedRooms.every((room) => typeof room.price === "number")
      : typeof currentPrice === "number")
  const canSave =
    hasHotel &&
    checkIn !== null &&
    checkOut !== null &&
    (alertMode === "fixed"
      ? Number.isFinite(target) && target > 0
      : canSavePercentTarget)

  const bookNowHref =
    effectivePriceEntityRef && checkIn && checkOut
      ? routes.hotelRooms.href({
          id: effectivePriceEntityRef,
          query: {
            start: dayObjectAsUrlParam(checkIn),
            end: dayObjectAsUrlParam(checkOut),
            adults,
            children: children > 0 ? children : undefined,
          },
        })
      : null

  const alreadyOnBookingPage =
    bookNowHref != null && router.asPath === bookNowHref

  const selectedRoomAlreadyAtTarget =
    alertMode === "fixed" &&
    Number.isFinite(target) &&
    selectedRooms.some(
      (room) => typeof room.price === "number" && room.price <= target
    )

  const dealAlreadyLive =
    alertMode === "fixed" &&
    canSave &&
    bookNowHref != null &&
    (selectedRooms.length > 0
      ? selectedRoomAlreadyAtTarget
      : currentPrice != null && currentPrice <= target)

  const savedDescription = (
    <span className="no-translate">
      {isMultiSpecificRoomSelection && alertMode === "percent"
        ? t("priceAlertSavedPercent", { percent: targetPercent })
        : t("priceAlertSavedBelow", {
            price: formatMoney(target, false, displayCurrency),
          })}
    </span>
  )

  const isOnPriceAlertsPage =
    router.isReady &&
    router.pathname === "/settings" &&
    singleParam(router.query.section) === SettingProactiveAgentsTab

  const priceAlertsActionProps = useMemo(
    () =>
      isOnPriceAlertsPage
        ? undefined
        : {
            children: "View alerts",
            onClick: () => {
              toastManager.close(PRICE_ALERT_TOAST_ID)
              void router.push(links.settings(SettingProactiveAgentsTab))
            },
          },
    [isOnPriceAlertsPage, router]
  )

  const showSaveSuccessToast = () => {
    toastManager.add({
      id: PRICE_ALERT_TOAST_ID,
      type: "success",
      title: isEditing ? "Changes saved" : "Alert saved",
      description: savedDescription,
      ...(priceAlertsActionProps
        ? {
            actionProps: priceAlertsActionProps,
            timeout: TOAST_WITH_ACTION_DURATION,
          }
        : {}),
    })
    onOpenChange(false)
  }

  const priceAlertAnalyticsProperties = useCallback(
    (
      source: string,
      overrides: Partial<{
        agent_id: number
        created_by: string
        created_count: number
        failed_count: number
        room_selection: "any_room" | "single_room" | "multiple_rooms"
        room_type_count: number
        status: string
      }> = {}
    ): EventPriceAlertProperties["properties"] => {
      const roomSelection =
        selectedRooms.length > 1
          ? "multiple_rooms"
          : selectedRooms.length === 1 || hasFixedRoom
            ? "single_room"
            : "any_room"

      return {
        agent_type: "hotel_price_alert" as const,
        source,
        created_by: activeAgent?.created_by ?? undefined,
        agent_id: activeAgent?.id,
        entity_ref: effectiveEntityRef,
        hotel_name: effectiveName,
        alert_mode: alertMode,
        target_price: Number.isFinite(target) ? target : undefined,
        room_selection: roomSelection,
        room_type_count:
          roomSelection === "any_room"
            ? 0
            : Math.max(selectedRooms.length, hasFixedRoom ? 1 : 0),
        check_in: checkIn ? dayObjectAsUrlParam(checkIn) : undefined,
        check_out: checkOut ? dayObjectAsUrlParam(checkOut) : undefined,
        adults,
        children,
        ...overrides,
      }
    },
    [
      activeAgent?.created_by,
      activeAgent?.id,
      adults,
      alertMode,
      checkIn,
      checkOut,
      children,
      effectiveEntityRef,
      effectiveName,
      hasFixedRoom,
      selectedRooms.length,
      target,
    ]
  )

  const trackedOpenRef = useRef(false)
  useEffect(() => {
    if (open && !trackedOpenRef.current) {
      trackedOpenRef.current = true
      sendAnalyticsEvent({
        name: "PriceAlertModalOpen",
        properties: priceAlertAnalyticsProperties(
          isEditing ? "edit_modal" : "create_modal"
        ),
      })
    } else if (!open) {
      trackedOpenRef.current = false
    }
  }, [isEditing, open, priceAlertAnalyticsProperties])

  const handleDateRangeChange = (range: DayRange) => {
    userModifiedSearchRef.current = true
    setCheckIn(range.from ?? null)
    setCheckOut(range.to ?? null)
    setCurrentPrice(undefined)
    setPriceEntityRef(null)
    setSelectedRoomKeys(["any"])
    if (!userModifiedTargetRef.current) {
      applyDefaultTargetsFromPrice(undefined)
    }
  }

  const activeTravelers = useMemo((): TravelerGroup => {
    if (activeRoom) return roomToTravelerGroup(activeRoom)
    return {
      adult_count: adults,
      child_count: children,
      infant_count: 0,
      pet_count: 0,
    }
  }, [activeRoom, adults, children])
  const travelersCount = totalPeople(activeTravelers)

  const handleRoomUpdate = (newRoom: Room) => {
    userModifiedSearchRef.current = true
    setActiveRoom(newRoom)
    setAdults(newRoom.adult_count)
    setChildren(newRoom.child_ages.length)
    setCurrentPrice(undefined)
    setPriceEntityRef(null)
    setSelectedRoomKeys(["any"])
    if (!userModifiedTargetRef.current) {
      applyDefaultTargetsFromPrice(undefined)
    }
  }

  const handleTravelersUpdate = (newTravelers: TravelerGroup) => {
    userModifiedSearchRef.current = true
    setAdults(newTravelers.adult_count)
    setChildren(newTravelers.child_count)
    setActiveRoom((currentRoom) =>
      currentRoom ? travelerGroupToRoom(newTravelers) : currentRoom
    )
    setCurrentPrice(undefined)
    setPriceEntityRef(null)
    setSelectedRoomKeys(["any"])
    if (!userModifiedTargetRef.current) {
      applyDefaultTargetsFromPrice(undefined)
    }
  }

  const handleRoomSelectionChange = (key: string, checked: boolean) => {
    userModifiedSearchRef.current = true
    if (key === "any") {
      setSelectedRoomKeys(["any"])
      setCurrentPrice(undefined)
      setPriceEntityRef(null)
      if (!userModifiedTargetRef.current) {
        applyDefaultTargetsFromPrice(undefined)
      }
      return
    }

    const withoutAny = selectedRoomKeys.filter((roomKey) => roomKey !== "any")
    const next = checked
      ? [...withoutAny, key]
      : withoutAny.filter((roomKey) => roomKey !== key)
    const normalizedNext = next.length > 0 ? next : ["any"]
    setSelectedRoomKeys(normalizedNext)
    setCurrentPrice(undefined)
    setPriceEntityRef(null)
    if (!userModifiedTargetRef.current) {
      applyDefaultTargetsFromPrice(defaultPriceForRoomKeys(normalizedNext))
    }
  }

  const handleSave = async () => {
    if (!checkIn || !checkOut || !canSave || saving || !effectiveEntityRef)
      return
    setActionError(null)
    setSaving(true)
    const buildTarget = (room: RoomSelection | null) => {
      if (alertMode === "fixed") return parsePositiveWholeNumber(targetPrice)
      const basePrice = room?.price ?? currentPrice
      if (
        typeof basePrice !== "number" ||
        !Number.isFinite(pct) ||
        pct <= 0 ||
        pct >= 100
      ) {
        return NaN
      }
      return Math.floor(basePrice * (1 - pct / 100))
    }
    // Observed nightly price at save time — shown in the alert list until the
    // first run records a price. Falls back to the stored baseline on edit
    // when no fresh price is available.
    const buildBaselinePrice = (room: RoomSelection | null) => {
      const fresh =
        room?.price ??
        (typeof knownCurrentPrice === "number" && !userModifiedSearchRef.current
          ? knownCurrentPrice
          : undefined) ??
        (typeof currentPrice === "number" ? currentPrice : undefined)
      return fresh ?? (isEditing ? agentCfg?.baseline_price : undefined)
    }
    const buildAgentConfig = (
      room: RoomSelection | null
    ): FixedPropertyConfig => {
      const baselinePrice = buildBaselinePrice(room)
      return {
        mode: "fixed_property",
        entity_ref: effectiveEntityRef,
        hotel_name: effectiveName,
        check_in: dayObjectAsUrlParam(checkIn),
        check_out: dayObjectAsUrlParam(checkOut),
        adults,
        children,
        ...(activeRoom ? { child_ages: activeRoom.child_ages } : {}),
        target_price: buildTarget(room),
        currency: room?.currency ?? currency,
        ...(typeof baselinePrice === "number"
          ? { baseline_price: baselinePrice }
          : {}),
        ...(room?.hotelRoomEntityRef
          ? { hotel_room_entity_ref: room.hotelRoomEntityRef }
          : {}),
        ...(room?.provider && room?.providerRoomId
          ? { provider: room.provider, provider_room_id: room.providerRoomId }
          : {}),
        ...(room?.roomName && (room.hotelRoomEntityRef || room.providerRoomId)
          ? { room_name: room.roomName }
          : {}),
      }
    }
    const roomsToCreate =
      selectedRooms.length > 0
        ? selectedRooms
        : fixedRoom
          ? [fixedRoom]
          : [null]
    const resolvedName = name.trim() || `${effectiveName} - Price Alert`
    const buildAgentName = (room: RoomSelection | null, multiRoom: boolean) =>
      room?.roomName && multiRoom
        ? `${effectiveName} - ${room.roomName} Price Alert`
        : resolvedName
    try {
      if (isGroupEdit && agent) {
        const desiredByKey = new Map<string, RoomSelection | null>()
        for (const room of roomsToCreate) {
          desiredByKey.set(roomMatchKey(room), room)
        }
        const existingByKey = new Map<string, AgentSummary>()
        for (const groupAgent of groupAgents) {
          const cfg =
            groupAgent.agent_type === "hotel_price_alert"
              ? groupAgent.agent_config
              : null
          existingByKey.set(agentMatchKey(cfg), groupAgent)
        }

        const updated: AgentSummary[] = []
        const created: AgentSummary[] = []
        const deleted: AgentSummary[] = []
        const failedRooms: string[] = []
        let firstError: unknown = null

        for (const [key, room] of desiredByKey) {
          const existing = existingByKey.get(key)
          const multiRoom = desiredByKey.size > 1
          try {
            if (existing) {
              const next = await api.update(existing.id, {
                name: buildAgentName(room, multiRoom),
                agent_config: buildAgentConfig(room),
              })
              updated.push(next)
            } else {
              const next = await api.createAgent({
                agent_type: "hotel_price_alert",
                agent_config: buildAgentConfig(room),
                name: buildAgentName(room, multiRoom),
              })
              created.push(next)
            }
          } catch (err) {
            firstError ??= err
            failedRooms.push(room?.roomName ?? effectiveName)
          }
        }

        for (const [key, existing] of existingByKey) {
          if (desiredByKey.has(key)) continue
          try {
            await api.destroy(existing.id)
            deleted.push(existing)
          } catch (err) {
            firstError ??= err
            const cfg =
              existing.agent_type === "hotel_price_alert"
                ? existing.agent_config
                : null
            failedRooms.push(cfg?.room_name ?? effectiveName)
          }
        }

        sendAnalyticsEvent({
          name: "PriceAlertUpdate",
          properties: priceAlertAnalyticsProperties("watch_price_modal", {
            agent_id: agent.id,
            created_by: agent.created_by ?? undefined,
            created_count: created.length,
            failed_count: failedRooms.length,
            room_selection:
              desiredByKey.size > 1
                ? "multiple_rooms"
                : roomsToCreate[0]
                  ? "single_room"
                  : "any_room",
            room_type_count: roomsToCreate.filter(Boolean).length,
            status: agent.status,
          }),
        })
        for (const next of updated) onUpdated?.(next)
        if (created.length > 0) onCreated?.(created)
        for (const removed of deleted) onDeleted?.(removed)

        if (failedRooms.length === 0) {
          showSaveSuccessToast()
        } else if (
          updated.length > 0 ||
          created.length > 0 ||
          deleted.length > 0
        ) {
          showSaveSuccessToast()
          toastManager.add({
            type: "error",
            description: `Couldn't save ${failedRooms.join(", ")}`,
          })
        } else if (firstError instanceof ClientValidationException) {
          setActionError(firstError.message)
        } else {
          setActionError(`Couldn’t save ${failedRooms.join(", ")}`)
        }
      } else if (isEditing && agent) {
        const [roomToUpdate, ...roomsToCreateAfterUpdate] = roomsToCreate
        const updated = await api.update(agent.id, {
          name: resolvedName,
          agent_config: buildAgentConfig(roomToUpdate),
          ...(isCustomizingAutoCreated ? { customize: true } : {}),
        })
        const created: AgentSummary[] = []
        const failedRooms: string[] = []
        let firstError: unknown = null
        for (const room of roomsToCreateAfterUpdate) {
          try {
            const newAgent = await api.createAgent({
              agent_type: "hotel_price_alert",
              agent_config: buildAgentConfig(room),
              name: room?.roomName
                ? `${effectiveName} - ${room.roomName} Price Alert`
                : resolvedName,
            })
            created.push(newAgent)
          } catch (err) {
            firstError ??= err
            failedRooms.push(room?.roomName ?? effectiveName)
          }
        }
        sendAnalyticsEvent({
          name: "PriceAlertUpdate",
          properties: priceAlertAnalyticsProperties("watch_price_modal", {
            agent_id: agent.id,
            created_by: agent.created_by ?? undefined,
            created_count: created.length,
            failed_count: failedRooms.length,
            room_selection:
              roomsToCreate.length > 1
                ? "multiple_rooms"
                : roomToUpdate
                  ? "single_room"
                  : "any_room",
            room_type_count: roomsToCreate.filter(Boolean).length,
            status: agent.status,
          }),
        })
        onUpdated?.(updated)
        if (created.length > 0) onCreated?.(created)
        if (failedRooms.length === 0) {
          showSaveSuccessToast()
        } else if (created.length > 0) {
          showSaveSuccessToast()
          toastManager.add({
            type: "error",
            description: `Couldn't save ${failedRooms.join(", ")}`,
          })
        } else if (firstError instanceof ClientValidationException) {
          setActionError(firstError.message)
        } else {
          setActionError(`Couldn’t save ${failedRooms.join(", ")}`)
        }
      } else {
        // Create each alert independently so one failure doesn't strand the
        // others silently — report exactly which room types failed.
        const created: AgentSummary[] = []
        const failedRooms: string[] = []
        let firstError: unknown = null
        for (const room of roomsToCreate) {
          try {
            const newAgent = await api.createAgent({
              agent_type: "hotel_price_alert",
              agent_config: buildAgentConfig(room),
              name:
                room?.roomName && roomsToCreate.length > 1
                  ? `${effectiveName} - ${room.roomName} Price Alert`
                  : resolvedName,
            })
            created.push(newAgent)
          } catch (err) {
            firstError ??= err
            failedRooms.push(room?.roomName ?? effectiveName)
          }
        }
        if (created.length > 0) {
          sendAnalyticsEvent({
            name: "PriceAlertCreate",
            properties: priceAlertAnalyticsProperties("watch_price_modal", {
              created_count: created.length,
              failed_count: failedRooms.length,
              room_selection:
                roomsToCreate.length > 1
                  ? "multiple_rooms"
                  : roomsToCreate[0]
                    ? "single_room"
                    : "any_room",
              room_type_count: roomsToCreate.filter(Boolean).length,
            }),
          })
          onCreated?.(created)
        }
        if (failedRooms.length === 0) {
          showSaveSuccessToast()
        } else if (created.length > 0) {
          showSaveSuccessToast()
          toastManager.add({
            type: "error",
            description: `Couldn't save ${failedRooms.join(", ")}`,
          })
        } else if (firstError instanceof ClientValidationException) {
          setActionError(firstError.message)
        } else {
          setActionError(
            failedRooms.length > 0
              ? `Couldn’t save ${failedRooms.join(", ")}`
              : "Couldn’t save the price alert"
          )
        }
      }
    } catch (err) {
      if (err instanceof ClientValidationException) {
        setActionError(err.message)
      } else {
        setActionError("Couldn’t save the price alert")
      }
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!activeAgent || deleting || saving) return
    setActionError(null)
    setDeleting(true)
    const agentsToDelete =
      isGroupEdit && groupAgents.length > 1 ? groupAgents : [activeAgent]
    try {
      const deleted: AgentSummary[] = []
      let firstError: unknown = null
      for (const groupAgent of agentsToDelete) {
        try {
          await api.destroy(groupAgent.id)
          deleted.push(groupAgent)
        } catch (err) {
          firstError ??= err
        }
      }
      if (deleted.length === 0) {
        if (firstError instanceof ClientValidationException) {
          setActionError(firstError.message)
        } else {
          setActionError("Couldn’t delete the price alert")
        }
        return
      }
      for (const removed of deleted) {
        sendAnalyticsEvent({
          name: "PriceAlertDelete",
          properties: priceAlertAnalyticsProperties("watch_price_modal", {
            agent_id: removed.id,
            created_by: removed.created_by ?? undefined,
            status: removed.status,
          }),
        })
        onDeleted?.(removed)
      }
      toastManager.add({
        type: "success",
        title: deleted.length > 1 ? "Alerts deleted" : "Alert deleted",
      })
      onOpenChange(false)
    } catch {
      setActionError("Couldn’t delete the price alert")
    } finally {
      setDeleting(false)
    }
  }

  const handleDelete = () => {
    if (!activeAgent || deleting || saving) return
    const deletingGroup = isGroupEdit && groupAgents.length > 1
    showDialogInfo({
      title: deletingGroup
        ? `Delete these ${groupAgents.length} price alerts?`
        : "Delete this price alert?",
      message: deletingGroup
        ? "You won’t be notified about price drops for these room types anymore."
        : "You won’t be notified about price drops for this stay anymore.",
      cancelLabel: "Cancel",
      okLabel: "Delete",
      okAction: () => void confirmDelete(),
    })
  }

  const travelersTrigger = (
    <button
      type="button"
      className="flex flex-1 flex-col outline-hidden hover:bg-subtle"
    >
      <span className="text-[.875em] text-muted">Guests</span>
      <span className="font-medium">{travelersCount}</span>
    </button>
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => onOpenChange(isOpen)}
      analyticsName="WatchPriceModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isCustomizingAutoCreated
              ? "Customize price alert"
              : isEditing
                ? "Edit price alert"
                : "Set up price alert"}
          </DialogTitle>
          <DialogDescription muted={false} className="text-balance">
            We’ll check Mindtrip hotel rates daily and alert you when one drops
            below your target.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-6">
          <div className="mb-7 flex items-center gap-2.5">
            <div className="relative size-10 shrink-0 overflow-hidden rounded-lg bg-foreground/5">
              {propertyPhoto ? (
                <Photo
                  src={propertyPhoto}
                  width="sm"
                  className="absolute inset-0 size-full"
                  alt=""
                />
              ) : (
                <div className="flex size-full items-center justify-center">
                  <EntityIcon type="hotel" className="size-4 text-muted" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 flex-col">
              <div className="truncate font-semibold">{effectiveName}</div>
              {propertyLocale ? (
                <div className="no-translate truncate text-xs text-muted">
                  {propertyLocale}
                </div>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 space-y-2">
            <div className="shrink-0 overflow-hidden rounded-xl border whitespace-nowrap">
              <div className="flex text-left text-sm leading-snug *:px-[1em] *:py-[.5em] *:not-first:border-l sm:text-base">
                <DatesDialog
                  initialDayRange={{
                    from: checkIn ?? undefined,
                    to: checkOut ?? undefined,
                  }}
                  earliestDay={browserToday()}
                  retDayCallback={handleDateRangeChange}
                  firstClickHint="from"
                >
                  <button
                    type="button"
                    className="flex flex-1 flex-col outline-hidden hover:bg-subtle"
                  >
                    <span className="text-[.875em] text-muted">Check in</span>
                    {checkIn ? (
                      <span key="date" className="no-translate font-medium">
                        {formatDayObjectAsMonthDay(checkIn)}
                      </span>
                    ) : (
                      <span key="empty" className="font-medium">
                        Select date
                      </span>
                    )}
                  </button>
                </DatesDialog>
                <DatesDialog
                  initialDayRange={{
                    from: checkIn ?? undefined,
                    to: checkOut ?? undefined,
                  }}
                  earliestDay={browserToday()}
                  retDayCallback={handleDateRangeChange}
                  firstClickHint="to"
                >
                  <button
                    type="button"
                    className="flex flex-1 flex-col outline-hidden hover:bg-subtle"
                  >
                    <span className="text-[.875em] text-muted">Check out</span>
                    {checkOut ? (
                      <span key="date" className="no-translate font-medium">
                        {formatDayObjectAsMonthDay(checkOut)}
                      </span>
                    ) : (
                      <span key="empty" className="font-medium">
                        Select date
                      </span>
                    )}
                  </button>
                </DatesDialog>
                {activeRoom ? (
                  <RoomDialog room={activeRoom} onRoomChange={handleRoomUpdate}>
                    {travelersTrigger}
                  </RoomDialog>
                ) : (
                  <TravelersDialog
                    travelers={activeTravelers}
                    retTravelersCallback={handleTravelersUpdate}
                  >
                    {travelersTrigger}
                  </TravelersDialog>
                )}
              </div>
            </div>
            {hasHotel &&
              checkIn !== null &&
              checkOut !== null &&
              currentPrice === null &&
              !priceFetching && (
                <p
                  role="alert"
                  className="rounded-lg bg-yellow-2 px-2.5 py-1.5 text-xs leading-tight text-pretty text-yellow-11"
                >
                  No availability for these dates
                </p>
              )}
          </div>

          {showRoomTypeSection && (
            <section>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-semibold">Room type</div>
                {roomOptionsLoading && (
                  <span className="animate-pulse text-xs text-muted">
                    Checking rooms…
                  </span>
                )}
              </div>
              {roomOptionsLoading ? (
                <div className="mt-1.5">
                  <SelectTriggerDisplayOnly
                    size="md"
                    aria-busy="true"
                    className="pointer-events-none cursor-default text-muted"
                  >
                    <span className="animate-pulse">Checking rooms…</span>
                  </SelectTriggerDisplayOnly>
                </div>
              ) : showRoomSelector ? (
                <div className="mt-1.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <SelectTriggerDisplayOnly size="md">
                          {roomSelectLabel}
                        </SelectTriggerDisplayOnly>
                      }
                    />
                    <DropdownMenuContent size="anchor">
                      <DropdownMenuCheckboxItem
                        checked={selectedRoomKeys.includes("any")}
                        label="Any room"
                        onCheckedChange={() =>
                          handleRoomSelectionChange("any", true)
                        }
                        className="items-start py-2"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            Any room
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-muted">
                            Best available price
                          </span>
                        </span>
                      </DropdownMenuCheckboxItem>

                      {rankedRoomOptions.map((room) => {
                        const detail = roomSelectionDetail(
                          room,
                          currency,
                          formatMoney
                        )

                        return (
                          <DropdownMenuCheckboxItem
                            key={room.key}
                            checked={selectedRoomKeys.includes(room.key)}
                            label={room.roomName}
                            onCheckedChange={(checked) =>
                              handleRoomSelectionChange(room.key, checked)
                            }
                            className="items-start py-2"
                          >
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <span className="font-medium">
                                {room.roomName}
                              </span>
                              {detail && (
                                <span className="truncate text-xs text-muted">
                                  {detail}
                                </span>
                              )}
                            </span>
                          </DropdownMenuCheckboxItem>
                        )
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {selectedRooms.length > 1 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {selectedRooms.map((room) => (
                        <SelectedRoomPill
                          key={room.key}
                          room={room}
                          onRemove={() =>
                            handleRoomSelectionChange(room.key, false)
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1.5 flex items-start gap-2 rounded-xl border px-2.5 py-2 text-xs">
                  <div className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-foreground/5">
                    {roomPhotoUrl ? (
                      <Photo
                        src={roomPhotoUrl}
                        width="sm"
                        className="absolute inset-0 size-full"
                        alt=""
                      />
                    ) : (
                      <IconBed className="size-4 text-gray-9" />
                    )}
                  </div>
                  <span className="min-w-0 flex-1 self-center">
                    <span className="block truncate font-medium">
                      {effectiveRoomName}
                    </span>
                  </span>
                </div>
              )}
            </section>
          )}

          <div>
            <div className="mb-1.5 flex items-baseline justify-between gap-2">
              <div className="text-sm font-semibold">
                {/* distinct keys to prevent Weglot crash on translation */}
                {alertMode === "fixed" ? (
                  <span key="fixed">Alert when price drops below</span>
                ) : (
                  <span key="percent">Alert when price drops by</span>
                )}
              </div>
              <div className="text-2xs text-muted sm:text-xs">
                <CurrentPriceHint
                  fetching={priceFetching}
                  price={displayCurrentPrice}
                  currency={displayCurrency}
                  ready={hasHotel && checkIn !== null && checkOut !== null}
                  selectedRoomCount={selectedRooms.length}
                />
              </div>
            </div>
            <InputGroup>
              <InputGroupAddon align="inline-start">
                <Select
                  value={alertMode}
                  onValueChange={(value) => {
                    if (value !== "fixed" && value !== "percent") return
                    setAlertMode(value)
                  }}
                >
                  <SelectTrigger
                    aria-label="Alert target type"
                    render={
                      <InputGroupButton
                        variant="subtle"
                        iconAfter={IconChevronDown}
                      >
                        {alertMode === "fixed" ? currencySymbol(currency) : "%"}
                      </InputGroupButton>
                    }
                  />
                  <SelectContent>
                    <SelectItem value="fixed">Fixed price</SelectItem>
                    <SelectItem value="percent">Percentage drop</SelectItem>
                  </SelectContent>
                </Select>
              </InputGroupAddon>
              {alertMode === "fixed" ? (
                <InputGroupInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="e.g. 250"
                  value={targetPrice}
                  onChange={(e) => {
                    userModifiedTargetRef.current = true
                    setTargetPrice(wholeNumberInputValue(e.target.value))
                  }}
                />
              ) : (
                <InputGroupInput
                  type="number"
                  min="1"
                  max="99"
                  step="1"
                  placeholder="e.g. 10"
                  value={targetPercent}
                  onChange={(e) => {
                    userModifiedTargetRef.current = true
                    setTargetPercent(e.target.value)
                  }}
                />
              )}
            </InputGroup>
            {/* distinct keys to prevent Weglot crash on translation */}
            <p className="mt-1 min-h-5 text-xs">
              {dealAlreadyLive ? (
                <span key="deal-live" className="text-green-11">
                  {selectedRooms.length > 1
                    ? "At least one selected room is already at your target."
                    : "Today’s price is already at your target."}
                </span>
              ) : isMultiSpecificRoomSelection && alertMode === "percent" ? (
                <span key="multi-percent" className="text-muted">
                  We’ll watch each selected room for a percentage drop from
                  today’s price.
                </span>
              ) : alertMode === "fixed" ? (
                <span key="fixed" className="text-muted">
                  Per night, in {currency}.
                </span>
              ) : Number.isFinite(percentTarget) ? (
                <span key="percent-target" className="text-muted">
                  Target:{" "}
                  <span className="font-medium text-foreground">
                    <span className="no-translate">
                      {formatMoney(percentTarget, false, currency)}
                    </span>
                    /night
                  </span>
                </span>
              ) : (
                <span key="percent-hint" className="text-muted">
                  Drop percentage from current nightly price.
                </span>
              )}
            </p>
          </div>
          <div>
            <label
              htmlFor="alert-name"
              className="mb-1.5 block text-sm font-semibold"
            >
              Alert name
            </label>
            <TextInput
              id="alert-name"
              value={name}
              placeholder={
                effectiveName
                  ? `${effectiveName} - Price Alert`
                  : "e.g. Tokyo Hyatt - Price Alert"
              }
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {actionError ? (
            <ErrorMessage className="text-sm">{actionError}</ErrorMessage>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <div className="flex gap-[inherit] sm:flex-1">
            {isEditing ? (
              <IconButton
                variant="outline"
                className="mr-auto"
                size="md"
                disabled={saving}
                loading={deleting}
                onClick={handleDelete}
                label="Delete alert"
                tooltip
                icon={IconTrash}
              />
            ) : null}
            <Button
              variant="ghost"
              disabled={deleting || saving}
              onClick={() => onOpenChange(false)}
              className={!isEditing ? "max-sm:w-full sm:ml-auto" : undefined}
            >
              Cancel
            </Button>
          </div>
          {dealAlreadyLive ? (
            <>
              <Button
                variant="outline"
                disabled={!canSave}
                loading={saving}
                onClick={() => void handleSave()}
              >
                Save anyway
              </Button>
              {bookNowHref && (
                <Button
                  variant="primary"
                  {...(alreadyOnBookingPage
                    ? { onClick: () => onOpenChange(false) }
                    : { href: bookNowHref })}
                >
                  Book now
                </Button>
              )}
            </>
          ) : (
            <Button
              disabled={!canSave || deleting}
              loading={saving}
              onClick={() => void handleSave()}
            >
              {isEditing ? "Save changes" : "Watch price"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CurrentPriceHint({
  fetching,
  price,
  currency,
  ready,
  selectedRoomCount,
}: {
  fetching: boolean
  price: number | null | undefined
  currency: string
  ready: boolean
  selectedRoomCount: number
}) {
  const formatMoney = Locale.useFormatMoney()
  if (!ready) return null
  if (selectedRoomCount > 1) {
    return <span>{selectedRoomCount} room types chosen</span>
  }
  if (price === undefined && fetching) {
    return <span className="animate-pulse">Checking price…</span>
  }
  if (price === undefined || price === null) return null
  return (
    <span>
      Currently{" "}
      <span className="font-medium text-foreground">
        <span className="no-translate">
          {formatMoney(price, false, currency)}
        </span>
        /night
      </span>
    </span>
  )
}

function SelectedRoomPill({
  room,
  onRemove,
}: {
  room: RoomSelection
  onRemove: () => void
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-foreground/5 px-3 py-2 text-2xs font-medium">
      <span className="min-w-0 truncate">{room.roomName}</span>
      <button
        type="button"
        className="-mr-0.5 ml-0.5 flex shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:text-foreground"
        aria-label={`Remove ${room.roomName}`}
        onClick={onRemove}
      >
        <IconCross strokeWidth={2.25} />
      </button>
    </span>
  )
}
